import React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import ListIcon from '@material-ui/icons/List';
import WidgetsOutlinedIcon from '@material-ui/icons/WidgetsOutlined';
import DropdownInput from 'components/DropdownInput';
import DropdownAutocomplete from 'components/DropdownAutocomplete';
import CheckboxButton from 'components/buttons/CheckboxButton';
import LoadingSpinner from 'components/LoadingSpinner';
import Button from '@material-ui/core/Button';
import {
  SORTING_OPTIONS,
  GROUP_OPTIONS,
  RADIO_OPTIONS,
  DISPLAY_OPTIONS,
  BARCHART_SORTING_OPTIONS
} from '../../content';

const useStyles = makeStyles(theme => ({
  buttonStyle: {
    width: '100%',
    height: '37px',
    backgroundColor: theme.palette.white,
    marginTop: '27px',
    border: `2px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    paddingLeft: '16px',
    boxSizing: 'border-box',

    color: theme.palette.grey.dark,
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      color: 'black'
    }
  },
  optionsTitles: {
    fontSize: '14px',
    color: theme.palette.black,
    fontWeight: '600',
    letterSpacing: 0,
    marginBottom: '6px'
  },
  gridStyle: {
    backgroundColor: theme.palette.accent.light,
    padding: '20px 12px 30px 12px',
    marginBottom: '20px'
  },
  gridItemMinWidth: {
    minWidth: '110px'
  }
}));

const HeatmapNavigation = ({
  handleHeatmapClick,
  isHeatmap,
  handleBarChartClick,
  dates,
  dateType,
  handleApiParamsChange,
  series,
  seriesType,
  groupType,
  sortType,
  displayType,
  handleSortChange,
  handleDisplayChange,
  valueRadioValue,
  handleRadioChange,
  setHeatmapAsHome,
  homepage
}) => {
  const classes = useStyles();
  const isAlreadySetAsHomepage = homepage === '/';

  return (
    <Grid xs={12} item container direction="row" spacing={1} className={classes.gridStyle}>
      <Grid item container direction="column" xs="auto" style={{ width: 'auto' }}>
        <Grid item xs="auto">
          <Typography className={classes.optionsTitles}>Mode</Typography>
        </Grid>
        <Grid item container direction="row" justify="flex-start">
          <Grid item xs="auto">
            <Box
              onClick={handleHeatmapClick}
              color={isHeatmap ? '#fff' : 'unset'}
              p={1}
              bgcolor={isHeatmap ? '#346C9F' : '#fff'}
              borderRadius="2px"
              border={isHeatmap ? '1px solid #346C9F' : '1px solid #E7E7E7'}
              maxWidth="40px"
              maxHeight="40px"
              boxShadow={isHeatmap ? '0px 3px 6px #346C9F66' : 'unset'}
              style={{ cursor: 'pointer' }}
            >
              <WidgetsOutlinedIcon color={isHeatmap ? 'inherit' : 'primary'} />
            </Box>
          </Grid>
          <Grid item xs="auto">
            <Box
              onClick={handleBarChartClick}
              color={!isHeatmap ? '#fff' : 'unset'}
              p={1}
              bgcolor={!isHeatmap ? '#346C9F' : '#fff'}
              borderRadius="2px"
              border={!isHeatmap ? '1px solid #346C9F' : '1px solid #E7E7E7'}
              maxWidth="40px"
              maxHeight="40px"
              boxShadow={!isHeatmap ? '0px 3px 6px #346C9F66' : 'unset'}
              style={{ cursor: 'pointer' }}
            >
              <ListIcon color={!isHeatmap ? 'inherit' : 'primary'} />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs="auto" className={classes.gridItemMinWidth}>
        <Typography className={classes.optionsTitles}>Date</Typography>
        {dates ? (
          <DropdownAutocomplete
            selected={dateType}
            options={dates}
            onSelect={handleApiParamsChange}
            minWidth="160px"
            type="dateType"
          />
        ) : (
          <div style={{ width: '140px', height: '40px' }}>
            <LoadingSpinner p={0} />
          </div>
        )}
      </Grid>
      <Grid item xs="auto" className={classes.gridItemMinWidth}>
        <Typography className={classes.optionsTitles}>Series</Typography>
        {series ? (
          <DropdownAutocomplete
            selected={seriesType}
            options={series}
            onSelect={handleApiParamsChange}
            minWidth="250px"
            type="seriesType"
          />
        ) : (
          <div style={{ width: '250px', height: '40px' }}>
            <LoadingSpinner p={0} />
          </div>
        )}
      </Grid>

      <Grid item xs="auto" className={classes.gridItemMinWidth}>
        <Typography className={classes.optionsTitles}>Group By</Typography>
        <DropdownInput
          selected={groupType}
          options={GROUP_OPTIONS}
          onSelect={handleApiParamsChange}
          label="Choose option..."
        />
      </Grid>
      <Grid item xs="auto" className={classes.gridItemMinWidth}>
        <Typography className={classes.optionsTitles}>Sort By</Typography>
        <DropdownInput
          selected={sortType}
          options={isHeatmap ? SORTING_OPTIONS : BARCHART_SORTING_OPTIONS}
          onSelect={handleSortChange}
          label="Choose option..."
        />
      </Grid>
      <Grid item xs="auto" className={classes.gridItemMinWidth}>
        <Typography className={classes.optionsTitles}>Display As</Typography>
        <DropdownInput
          selected={displayType}
          options={DISPLAY_OPTIONS}
          onSelect={handleDisplayChange}
          label="Choose option..."
        />
      </Grid>
      {!isAlreadySetAsHomepage && (
        <Grid item xs="auto" className={classes.gridItemMinWidth}>
          <Button
            variant="contained"
            color="default"
            className={classes.buttonStyle}
            onClick={setHeatmapAsHome}
          >
            Set As Homepage
          </Button>
        </Grid>
      )}

      <Grid item xs={5} md={12} lg={12} xl={5}>
        <Typography className={classes.optionsTitles}>Show</Typography>
        <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={2}>
          {RADIO_OPTIONS &&
            RADIO_OPTIONS.map(option => (
              <Grid item xs="auto" key={option.name}>
                <CheckboxButton
                  id={option.name}
                  title={option.title}
                  checked={valueRadioValue[option.name]}
                  onChange={handleRadioChange}
                  name={option.name}
                  hasTransparentBg
                />
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
HeatmapNavigation.propTypes = {
  handleHeatmapClick: PropTypes.func.isRequired,
  isHeatmap: PropTypes.bool.isRequired,
  handleBarChartClick: PropTypes.func.isRequired,
  dates: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  dateType: PropTypes.string.isRequired,
  handleApiParamsChange: PropTypes.func.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })
  ),
  seriesType: PropTypes.number.isRequired,
  groupType: PropTypes.string.isRequired,
  sortType: PropTypes.string.isRequired,
  displayType: PropTypes.string.isRequired,
  handleSortChange: PropTypes.func.isRequired,
  handleDisplayChange: PropTypes.func.isRequired,
  valueRadioValue: PropTypes.shape({
    portfolios: PropTypes.bool.isRequired,
    indexes: PropTypes.bool.isRequired,
    showProposedManagers: PropTypes.bool.isRequired
  }).isRequired,
  handleRadioChange: PropTypes.func.isRequired
};
HeatmapNavigation.defaultProps = {
  dates: null,
  series: null
};
export default HeatmapNavigation;
